import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Dialog as DetailDialog, DialogContent as DetailContent, DialogHeader as DetailHeader, DialogTitle as DetailTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { fmt } from "@/lib/api";

export default function ClientsAdmin() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [users, setUsers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "team" });
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState("");

  const openClient = async (email) => {
    try {
      const { data } = await api.get(`/admin/clients/${encodeURIComponent(email)}`);
      setDetail(data);
      setNotes(data.client?.notes || "");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveNotes = async () => {
    try {
      await api.put(`/admin/clients/${encodeURIComponent(detail.client.email)}/notes`, { notes });
      toast.success("Notes saved.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const refresh = () => {
    api.get("/admin/clients").then((r) => setClients(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/consultations").then((r) => setConsultations(r.data)).catch(() => {});
    if (user?.role === "admin") api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  };
  useEffect(refresh, [user]);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", newUser);
      toast.success("Team member added.");
      setAdding(false);
      setNewUser({ name: "", email: "", password: "", role: "team" });
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-clients">
      <h1 className="font-display text-4xl text-white mb-8">Clients & Team</h1>
      <Tabs defaultValue="clients">
        <TabsList className="bg-[#0A0A0A] border border-white/10 mb-6">
          <TabsTrigger data-testid="tab-clients" value="clients">Clients ({clients.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-consultations" value="consultations">Consultations ({consultations.length})</TabsTrigger>
          {user?.role === "admin" && <TabsTrigger data-testid="tab-team" value="team">Team ({users.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="clients">
          {clients.length === 0 ? <p className="text-sm text-zinc-500">No clients captured yet.</p> : (
            <div className="border border-white/10 divide-y divide-white/5">
              {clients.map((c) => (
                <button key={c.id} data-testid={`client-row-${c.id}`} onClick={() => openClient(c.email)} className="w-full text-left bg-[#0A0A0A] p-4 flex flex-wrap justify-between gap-3 hover:bg-white/5 transition-colors">
                  <div>
                    <p className="text-sm text-white font-medium">{c.name || "—"}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{c.email} · {c.phone || "no phone"}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {c.ig && <>IG: {c.ig} · </>}
                      {c.tiktok && <>TT: {c.tiktok} · </>}
                      {c.youtube && <>YT: {c.youtube} · </>}
                      {c.spotify && <>SP: {c.spotify}</>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-widest text-brand">{(c.sources || []).join(" · ")}</p>
                    <p className="text-xs text-zinc-600 mt-1">{c.updated_at ? new Date(c.updated_at).toLocaleDateString() : ""}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="consultations">
          {consultations.length === 0 ? <p className="text-sm text-zinc-500">No consultation requests yet.</p> : (
            <div className="border border-white/10 divide-y divide-white/5">
              {consultations.map((c) => (
                <div key={c.id} data-testid={`consultation-row-${c.id}`} className="bg-[#0A0A0A] p-4">
                  <div className="flex flex-wrap justify-between gap-2">
                    <p className="text-sm text-white font-medium">{c.name}</p>
                    <p className="text-xs text-zinc-600">{new Date(c.created_at).toLocaleString()}</p>
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5">{c.email} · {c.phone} · Interest: {c.service_interest || "—"}</p>
                  {c.message && <p className="text-xs text-zinc-400 mt-2">"{c.message}"</p>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {user?.role === "admin" && (
          <TabsContent value="team">
            <div className="flex justify-end mb-4">
              <button data-testid="add-user-btn" onClick={() => setAdding(true)}
                className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase px-5 py-3 transition-colors">
                <Plus className="w-4 h-4" /> Add Team Member
              </button>
            </div>
            <div className="border border-white/10 divide-y divide-white/5">
              {users.map((u) => (
                <div key={u.email} data-testid={`user-row-${u.email}`} className="bg-[#0A0A0A] p-4 flex justify-between">
                  <div>
                    <p className="text-sm text-white font-medium">{u.name}</p>
                    <p className="text-xs text-zinc-500">{u.email}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-brand self-center">{u.role}</span>
                </div>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>

      <DetailDialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DetailContent data-testid="client-detail-dialog" className="bg-[#0A0A0A] border-white/10 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DetailHeader>
            <DetailTitle className="font-display text-3xl text-white">{detail?.client?.name || detail?.client?.email}</DetailTitle>
          </DetailHeader>
          {detail && (
            <div className="space-y-6">
              <div className="text-xs text-zinc-400 space-y-1">
                <p>{detail.client.email} · {detail.client.phone || "no phone"}</p>
                <p>{[["IG", detail.client.ig], ["TikTok", detail.client.tiktok], ["X", detail.client.x], ["YT", detail.client.youtube], ["Spotify", detail.client.spotify]].filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ") || "No socials"}</p>
                {detail.client.saved_card?.last4 && <p className="text-brand">Saved card: {detail.client.saved_card.brand} •••• {detail.client.saved_card.last4}</p>}
                <p className="text-zinc-600">Sources: {(detail.client.sources || []).join(", ") || "—"}</p>
              </div>
              <div>
                <h3 className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-2">Bookings ({detail.bookings.length})</h3>
                {detail.bookings.map((b) => (
                  <p key={b.id} className="text-xs text-zinc-300 py-1.5 border-b border-white/5">
                    {b.type} · {b.listing_title || b.details?.job_type || "—"} · {fmt(b.estimate)} · <span className="text-brand">{b.status.replace(/_/g, " ")}</span> · {new Date(b.created_at).toLocaleDateString()}
                  </p>
                ))}
                {detail.bookings.length === 0 && <p className="text-xs text-zinc-600">None</p>}
              </div>
              <div>
                <h3 className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-2">Orders ({detail.orders.length})</h3>
                {detail.orders.map((o) => (
                  <p key={o.id} className="text-xs text-zinc-300 py-1.5 border-b border-white/5">
                    {o.items?.map((i) => i.name).join(", ")} · {fmt(o.total)} · <span className="text-brand">{o.status.replace(/_/g, " ")}</span> · {new Date(o.created_at).toLocaleDateString()}
                  </p>
                ))}
                {detail.orders.length === 0 && <p className="text-xs text-zinc-600">None</p>}
              </div>
              <div>
                <h3 className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-2">Activity ({detail.events.length})</h3>
                <div className="max-h-40 overflow-y-auto">
                  {detail.events.map((e) => (
                    <p key={e.id} className="text-xs text-zinc-400 py-1 border-b border-white/5">
                      <span className="text-zinc-500">{new Date(e.created_at).toLocaleString()}</span> — {e.event.replace(/_/g, " ")} {e.detail && `· ${e.detail}`}
                    </p>
                  ))}
                  {detail.events.length === 0 && <p className="text-xs text-zinc-600">No tracked activity</p>}
                </div>
              </div>
              <div>
                <h3 className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-2">Internal Notes</h3>
                <Textarea data-testid="client-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-white/5 border-white/10" />
                <button data-testid="client-notes-save" onClick={saveNotes}
                  className="mt-3 bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase px-5 py-2.5 transition-colors">
                  Save Notes
                </button>
              </div>
            </div>
          )}
        </DetailContent>
      </DetailDialog>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent data-testid="user-dialog" className="bg-[#0A0A0A] border-white/10 text-zinc-100 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">New Team Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={createUser} className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-400">Name</Label>
              <Input data-testid="user-name" required value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Email</Label>
              <Input data-testid="user-email" type="email" required value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Password</Label>
              <Input data-testid="user-password" type="password" required minLength={8} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="user-role" className="bg-white/5 border-white/10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <button data-testid="user-save-btn" type="submit"
              className="w-full bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase py-3 transition-colors">
              Create Account
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
